import deepEqual from 'deep-equal'
import {DeepPartial} from 'ts-essentials'
import {logger} from './logger'
import {Util} from './util'

export class ObjUtil {
  public static isDefined<T>(obj: T | undefined): obj is T {
    return !(
      !obj &&
      (obj as any) !== 0 &&
      (obj as any) !== false &&
      (obj as any) !== ''
    )
  }

  public static keys<T>(obj: T): Array<Extract<keyof T, string>> {
    return obj ? (Object.keys(obj) as Array<Extract<keyof T, string>>) : []
  }

  public static update<T, K extends keyof T>(
    obj: T,
    key: K,
    cb: (item: T[K] | undefined) => any
  ): T {
    const value = obj[key]
    const newValue = cb(value)
    return {...obj, [key]: newValue}
  }

  public static values<T>(obj: T): Array<T[keyof T]> {
    return obj
      ? Object.keys(obj).map((key: string) => {
          const k = key as keyof T
          return obj[k]
        })
      : []
  }

  public static entries<T>(obj: T): Array<[keyof T, T[keyof T]]> {
    return obj
      ? Object.keys(obj).map((key: string) => {
          const k = key as keyof T
          return [k, obj[k]] as [keyof T, T[keyof T]]
        })
      : []
  }

  public static list<T, U>(obj: T, fn: (k: keyof T, v: T[keyof T]) => U): U[] {
    if (!obj) {
      return []
    }
    return Object.keys(obj).map(key => {
      const k = key as keyof T
      return fn(k, obj[k])
    })
  }

  public static fromKeys<V, K extends string>(
    arr: K[],
    fn: (key: K, index: number) => V
  ): {[key: string]: V} {
    if (!arr) {
      return {}
    }
    const res: {[key: string]: V} = {}
    arr.forEach((key, index) => (res[key] = fn(key, index)))
    return res
  }

  public static map<T, U>(
    obj: T,
    mapper: (k: keyof T, v: T[keyof T]) => U
  ): {[key in keyof T]: U} {
    if (!obj || !mapper) {
      logger.warn('Incorrect mapping arguments')
      return undefined as any
    }
    const res: {[key in keyof T]: U} = {} as {[key in keyof T]: U}
    Object.keys(obj).forEach(key => {
      const k = key as keyof T
      res[k] = mapper(k, obj[k])
    })
    return res
  }

  public static forEach<T>(
    obj: T,
    fn: (k: keyof T, v: T[keyof T]) => void
  ): void {
    if (!obj) {
      return
    }
    Object.keys(obj).forEach(key => {
      const k = key as Extract<keyof T, string>
      fn(k, obj[k])
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  public static filter<T extends {}>(
    obj: T,
    filter: (k: keyof T, v: T[keyof T]) => boolean
  ): Partial<T> {
    if (!obj) {
      return {}
    }
    const res: Partial<T> = {}
    ObjUtil.forEach(obj, (k, v) => {
      if (filter(k, v)) {
        res[k] = v
      }
    })
    return res
  }

  public static pick<T, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
    const ret: any = {}
    keys.forEach(key => {
      if (key in (obj as Record<string, unknown>)) {
        ret[key] = obj[key]
      }
    })
    return ret
  }

  /**
   * Remove null, undefined and NaN values from object recursively
   */
  public static removeEmptyValues<T>(o: T): DeepPartial<T> {
    if (typeof o !== 'object') {
      return o as DeepPartial<T>
    }
    const cloned = Util.clone(o)
    Object.keys(cloned).forEach(k => {
      const val = (cloned as any)[k]
      if (val === undefined || val === null || Object.is(val, NaN)) {
        delete (cloned as any)[k]
      } else if (typeof val === 'object') {
        ;(cloned as any)[k] = ObjUtil.removeEmptyValues(val)
      }
    })
    return cloned as any
  }

  public static shallowEqual<T>(a: T, b: T): boolean {
    if (a === b) {
      return true
    }
    if (!a || !b) {
      return false
    }
    const keysA = Object.keys(a) as Array<keyof T>
    const keysB = Object.keys(b) as Array<keyof T>
    if (keysA.length !== keysB.length) {
      return false
    }
    for (const key of keysA) {
      if (a[key] !== b[key]) {
        return false
      }
    }
    return true
  }

  public static deepEqual<T>(a: T, b: T, noNullable: boolean): boolean {
    if (!noNullable) {
      return deepEqual(a, b)
    }
    if (a === b) {
      return true
    }
    if (!a && !b) {
      return true
    }
    if (!a || !b) {
      return false
    }
    if (typeof a === 'object' && typeof b === 'object') {
      const a1 = ObjUtil.removeEmptyValues(a)
      const b1 = ObjUtil.removeEmptyValues(b)
      return deepEqual(a1, b1)
    } else {
      return deepEqual(a, b)
    }
  }

  /**
   * Fast check if object has no fields.
   * (I failed to find faster)
   */
  public static isEmpty(obj: any): boolean {
    if (!obj?.hasOwnProperty) {
      return true
    }
    for (const k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) {
        continue
      }
      return false
    }
    return true
  }

  public static getChangedKeys<T>(
    o1: T | undefined,
    o2: T | undefined
  ): Set<keyof T> {
    const obj1: Partial<T> = o1 || {}
    const obj2: Partial<T> = o2 || {}
    const res: Set<keyof T> = new Set()

    ObjUtil.forEach(obj1, k => {
      if (!(k in obj2) || !ObjUtil.deepEqual(obj1[k], obj2[k], true)) {
        res.add(k)
      }
    })

    ObjUtil.forEach(obj2, k => {
      if (!(k in obj1)) {
        res.add(k)
      }
    })

    return res
  }

  public static getAddedKeys<T>(o1: T, o2: T): Set<keyof T> {
    return this.getDeletedKeys(o2, o1)
  }

  public static getDeletedKeys<T>(o1: T, o2: T): Set<keyof T> {
    const obj1: Partial<T> = o1 || {}
    const obj2: Partial<T> = o2 || {}
    const res: Set<keyof T> = new Set()

    ObjUtil.forEach(obj1, k => {
      if (
        obj1[k] !== null &&
        obj1[k] !== undefined &&
        (!(k in obj2) || obj2[k] === null || obj2[k] === undefined)
      ) {
        res.add(k)
      }
    })
    return res
  }

  public static omit<T, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> {
    if (!obj) {
      return obj
    }
    const newObj = Util.clone(obj)
    for (const key of keys) {
      delete newObj[key]
    }

    return newObj
  }

  public static omitFastNoClone<T, K extends keyof T>(
    obj: T,
    ...keys: K[]
  ): Omit<T, K> {
    if (!obj) {
      return obj
    }
    const newObj = {...obj}
    for (const key of keys) {
      delete newObj[key]
    }

    return newObj
  }

  public static deletedAtKey<T>(obj: T, key: keyof T): T {
    const {[key]: _, ...newObj} = obj as any
    return newObj
  }
}
